import React, { useEffect, useState } from "react";
import { Search, X, Check, Lock } from "lucide-react";
import api from "../lib/apiClient";

// Searchable multi-select of ALL supported brands. Selectable brands have approved sizing;
// unavailable brands stay visible but disabled with a truthful reason (never implied supported).
// With `options` (from /api/brands/ready) only brands with verified sizing coverage for the current selection are listed —
// no imported/researched brands, no hard-coded count; pending charts appear only for authorized preview testers, labelled.
export function BrandMultiSelect({ value, onChange, testidPrefix = "brand", options, loading, message, state, onRetry }) {
  const [fetched, setFetched] = useState([]);
  const [q, setQ] = useState("");
  const stored = value || [];
  const ready = Array.isArray(options);
  // Names not offered on this surface (e.g. brands saved before the launch list narrowed) stay stored but are never shown.
  const known = new Set((ready ? options : fetched).map((b) => b.name));
  const sel = (ready || fetched.length) ? stored.filter((n) => known.has(n)) : stored;
  const hidden = stored.filter((n) => !sel.includes(n));

  useEffect(() => { if (!ready) api.get("/brands").then(({ data }) => setFetched(data.brands || [])).catch(() => {}); }, [ready]);
  const all = ready ? options.map((b) => ({ name: b.name, selectable: true, tier: b.tier, label: b.label, covers_all_children: b.covers_all_children, recommendation_ready: b.recommendation_ready })) : fetched.map((b) => ({ ...b, selectable: true }));
  const verifiedCount = ready ? all.filter((b) => b.tier === "verified").length : 0;
  const pendingCount = ready ? all.length - verifiedCount : 0;
  const CAP_LABEL = { suggested_fit: "Official chart guidance", size_guide: "Size guide only", none: "Sizing coming" };
  if (ready && loading) return <p className="text-xs text-ink-muted mt-2" data-testid={`${testidPrefix}-loading`}>Checking which brands have verified sizing for this selection…</p>;
  if (ready && all.length === 0) {
    return (
      <div className="mt-2 rounded-2xl border border-line bg-cream/50 p-3 text-xs text-ink-soft" data-testid={`${testidPrefix}-${state === "error" ? "error" : state === "no_children" ? "no-children" : "none"}`} role={state === "error" ? "alert" : undefined}>
        {message || "No brand has verified sizing for this selection yet."}
        {state === "error" && onRetry && <button type="button" onClick={onRetry} data-testid={`${testidPrefix}-retry`} className="ml-2 inline-flex min-h-[32px] items-center rounded-full border border-brand px-3 font-bold text-brand bg-white">Try again</button>}
        {state !== "no_children" && sel.length > 0 && <p className="mt-1 text-ink-muted">Previously chosen: {sel.join(", ")} — not applied to this search.</p>}
      </div>
    );
  }

  const toggle = (name) => onChange([...hidden, ...(sel.includes(name) ? sel.filter((x) => x !== name) : [...sel, name])]);
  const filtered = all.filter((b) => b.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div data-testid={`${testidPrefix}-multiselect`} className="mt-2">
      {sel.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testidPrefix}-selected`}>
          {sel.map((name) => (
            <span key={name} data-testid={`${testidPrefix}-chip-${name}`} className="inline-flex items-center gap-1 h-8 pl-3 pr-1.5 rounded-full bg-brand text-white text-xs font-bold">
              {name}
              <button onClick={() => toggle(name)} aria-label={`Remove ${name}`} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-white/25"><X size={13} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <input
          data-testid={`${testidPrefix}-search`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search brands…"
          aria-label="Search brands"
          className="w-full h-11 pl-9 pr-3 rounded-2xl border-2 border-line bg-white focus:border-brand outline-none text-sm"
        />
      </div>
      <p className="text-xs text-ink-soft mt-1" data-testid={`${testidPrefix}-count`}>{ready ? (verifiedCount ? `${verifiedCount} brand${verifiedCount === 1 ? "" : "s"} with a verified official chart for this selection${all.some((b) => b.tier === "verified" && b.recommendation_ready) ? "" : " (chart reference)"}.${pendingCount ? ` ${pendingCount} more shown as pending verification (preview only).` : ""}` : `No brand has a verified official chart for this selection yet.${pendingCount ? ` ${pendingCount} shown as pending verification (preview only) — not verified coverage.` : ""}`) : `${all.length} launch brand${all.length === 1 ? "" : "s"}. Pick the ones you like — what each chart covers is shown beside it.`}</p>
      <div className="mt-2 max-h-56 overflow-y-auto rounded-2xl border border-line divide-y divide-line" role="listbox" aria-label="Brands">
        {filtered.map((b) => {
          const on = sel.includes(b.name);
          return (
            <button
              key={b.name}
              type="button"
              role="option"
              aria-selected={on}
              aria-disabled={!b.selectable}
              disabled={!b.selectable}
              onClick={() => b.selectable && toggle(b.name)}
              data-testid={`${testidPrefix}-opt-${b.name}`}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                !b.selectable ? "text-ink-muted/60 bg-cream/40 cursor-not-allowed"
                : on ? "bg-blush text-ink font-bold" : "text-ink-soft hover:bg-cream"
              }`}
            >
              <span className="flex flex-col min-w-0">
                <span className="flex items-center gap-2 min-w-0">
                  {!b.selectable && <Lock size={12} className="shrink-0" />}
                  <span className="truncate">{b.name}</span>
                </span>
                {!ready && b.capability && <span className={`text-[10px] font-semibold ${b.capability === "suggested_fit" ? "text-success" : b.capability === "size_guide" ? "text-ink-muted" : "text-gold-text"}`} data-testid={`${testidPrefix}-cap-${b.name}`}>{b.capability === "suggested_fit" ? "Official chart guidance" : (b.coverage_text || CAP_LABEL[b.capability])}</span>}
                {b.label && <span className={`text-[10px] font-semibold ${b.tier === "verified" ? "text-ink-muted" : "text-gold-text"}`} data-testid={`${testidPrefix}-label-${b.name}`}>{b.label}</span>}
              </span>
              {b.selectable && (on ? <Check size={16} className="text-brand shrink-0" /> : <span className={`text-[10px] font-semibold shrink-0 text-right ${b.tier === "pending_owner_verification" ? "text-gold-text" : "text-success"}`} data-testid={`${testidPrefix}-tier-${b.name}`}>{b.tier === "pending_owner_verification" ? "Pending verification" : b.recommendation_ready === false ? "Verified chart · reference only" : "Official chart"}</span>)}
            </button>
          );
        })}
        {filtered.length === 0 && <p className="px-3 py-3 text-sm text-ink-muted">No brands match “{q}”.</p>}
      </div>
    </div>
  );
}
